import "./user.css";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUsers, updateUser } from "../../redux/apiCalls";
import moment from "moment";
import styled from "styled-components";
import Select from "react-select";
import { commonSelectProps } from "../../components/shared/SelectStyles";
import { FiEdit2, FiPlus, FiX } from "react-icons/fi";
import { publicRequest } from "../../requestMethods";

const branchOptions = [
    { label: "Own Branch", value: "own" },
    { label: "All Branch", value: "all" },
];

const statusOptions = [
    {
        label: "Inactive",
        value: "inactive",
    },
    {
        label: "Active",
        value: "active",
    },
    {
        label: "All",
        value: "",
    },
];

const ModalContainer = styled.div`
    height: 100%;
    width: 100%;
    opacity: 0.75;
    background: black;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    position: fixed;
    z-index: 100;
`;

const OverlayContainer = styled.div`
    overflow: hidden;

    /* Allow vertical scrolling for taller content on mobile */
    @media (max-width: 768px) {
        overflow-y: auto;
        max-height: 100vh;
        -webkit-overflow-scrolling: touch;
    }
`;

const Overlay = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    color: white;
    background-color: transparent;
    visibility: visible;
    z-index: 101;

    /* Add breathing room on desktop so the modal does not touch edges */
    @media (min-width: 769px) {
        padding: 48px 24px;
        box-sizing: border-box;
    }

    /* Add top padding on mobile to avoid overlap with navbar */
    @media (max-width: 768px) {
        padding-top: 80px;
        align-items: flex-start;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
    }
`;

const ModalDetails = styled.div`
    background-color: transparent;
    color: black;
    width: 55%;
    max-width: 820px;
    display: flex;
    justify-content: center;
    z-index: 102;

    /* Desktop spacing to keep container away from top/bottom */
    @media (min-width: 769px) {
        height: auto;
        max-height: calc(100vh - 96px);
    }

    /* Make the popup wider on small screens without affecting desktop */
    @media (max-width: 768px) {
        width: 90%;
        height: auto;
        max-height: 90vh;
        align-items: flex-start;
    }
`;

const ModalItemContainer = styled.div`
    background-color: white;
    color: black;
    width: 100%;
    display: grid;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
    box-sizing: border-box;
    max-height: calc(100vh - 200px);
    overflow-y: auto;

    /* Desktop: same card layout as the Admins edit form */
    @media (min-width: 769px) {
        max-height: calc(100vh - 96px);
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        padding: 32px;
        gap: 12px;

        & .addProductForm {
            width: 100%;
            max-width: 760px;
            margin: 0 auto;
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 16px 24px;
        }

        & .addProductItem {
            width: 100%;
        }

        /* Let wider fields span full width */
        & .addProductItem:last-of-type {
            grid-column: 1 / -1;
        }

        & .updateProductButton {
            grid-column: 1 / -1;
            justify-self: center;
            width: 240px;
        }
    }

    /* Improve mobile spacing and input widths inside the modal only */
    @media (max-width: 768px) {
        height: auto;
        min-height: fit-content;
        max-height: 90vh;
        overflow-y: auto;
        padding: 12px;
        display: flex;
        flex-direction: column;

        & .addProductForm {
            width: 100%;
            display: flex;
            flex-direction: column;
        }

        & .addProductItem {
            width: 100%;
        }

        & .addProductItem input,
        & .addProductItem select,
        & .addProductItem textarea {
            width: 100%;
            box-sizing: border-box;
        }

        & .updateProductButton {
            margin-top: 20px;
            margin-bottom: 12px;
        }
    }
`;

const DesktopCloseButton = styled.button`
    display: none;
    border: none;
    background: transparent;
    color: #111827;
    padding: 8px;
    font-size: 22px;
    cursor: pointer;
    align-self: flex-start;

    @media (min-width: 769px) {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 4px;
    }
`;

const MobileCloseButton = styled.button`
    display: none;
    border: none;
    background: transparent;
    color: #111827;
    padding: 6px 8px;
    font-size: 22px;
    cursor: pointer;
    align-self: flex-start;

    @media (max-width: 768px) {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 8px;
    }
`;

export default function User() {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.user.currentUser);
    const isMarketer = currentUser?.type === "marketer";
    const accountId = (value) => String(value?._id || value || "");
    const canEditListedUser = (account) => {
        const ownerId = accountId(account?.principle);
        if (!ownerId) return false;
        return ownerId === accountId(currentUser?._id) || ownerId === accountId(currentUser?.principle);
    };
    const [inputs, setInputs] = useState({
        status: undefined,
        type: "user",
        limit: 20,
        page: 1,
        name: undefined,
        branch: "own",
    });
    const [user, setUser] = useState(undefined);
    const [name, setName] = useState(undefined);
    const [editModalState, setEditModalState] = useState(false);
    const [usersData, setUsersData] = useState([]);
    const [marketersData, setMarketersData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const filters = { ...inputs };
                if (inputs.branch !== "all") {
                    const principleId = currentUser?.principle
                        ? accountId(currentUser.principle)
                        : accountId(currentUser?._id);
                    if (principleId) filters.principle = principleId;
                }
                delete filters.branch;

                const result = await getUsers(dispatch, filters);
                setUsersData(result ? result.users : []);
                setTotalPages(result.totalPages);
                setIsLoaded(true);
            } catch (error) {
                console.error("Error fetching users:", error);
            }
        };

        fetchData();
    }, [dispatch, inputs, currentUser]);

    useEffect(() => {
        const getMarketers = async () => {
            try {
                let principleParam;

                if (!currentUser?.principle) {
                    if (currentUser?.type === "outright") {
                        principleParam = currentUser._id;
                    } else if (currentUser?.superAdmin) {
                        principleParam = undefined;
                    } else {
                        principleParam = currentUser?.principle;
                    }
                }

                const params = {
                    type: ["marketer"],
                };
                if (principleParam !== undefined) {
                    params.principle = principleParam;
                }

                const res = await publicRequest.get("/users/admin", {
                    params,
                });
                setMarketersData(res.data);
            } catch (error) {
                console.error("Error fetching marketers:", error);
            }
        };
        getMarketers();
    }, [currentUser]);

    const handleChange = (e) => {
        setUser((prev) => {
            return { ...prev, [e.target.name]: e.target.value };
        });
    };

    const handleNameChange = (e) => {
        setIsLoaded(false);
        setInputs((prev) => {
            return { ...prev, page: 1, name: e.target.value };
        });
        setName(e.target.value);
    };

    const handleInputStatus = (e) => {
        setIsLoaded(false);
        setInputs((prev) => {
            return { ...prev, page: 1, status: e.value };
        });
    };

    const handleResetFilters = () => {
        setInputs({
            status: undefined,
            type: "user",
            limit: 20,
            page: 1,
            name: undefined,
            branch: "own",
        });
        setName(undefined);
        setIsLoaded(false);
    };

    const handleClick = async () => {
        if (!canEditListedUser(user)) return;

        // Create a copy of the user object
        const userToUpdate = { ...user };
        
        // Remove password field if it's empty, undefined, or not provided
        if (!userToUpdate.password || userToUpdate.password.trim() === '') {
            delete userToUpdate.password;
        }
    
        updateUser(user._id, userToUpdate, dispatch);
    };

    const columns = [
        {
            Header: "Name",
            accessor: "name",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value}</div>
            ),
        },
        {
            Header: "Phone Number",
            accessor: "phoneNumber",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value}</div>
            ),
        },
        {
            Header: "IC",
            accessor: "ic",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value ? value : ""}</div>
            ),
        },
        {
            Header: "TIN",
            accessor: "tin",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value ? value : ""}</div>
            ),
        },
        {
            Header: "Email",
            accessor: "email",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value ? value : ""}</div>
            ),
        },
        {
            Header: "Type",
            accessor: "type",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">{value}</div>
            ),
        },
        {
            Header: "Status",
            accessor: "status",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">{value}</div>
            ),
        },
        {
            Header: "Member Since",
            accessor: "createdAt",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {moment(value).format("DD/MM/YYYY")}
                </div>
            ),
        },
        {
            Header: "Action",
            accessor: "action",
            Cell: (params) =>
                canEditListedUser(params.row.original) ? (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                        type="button"
                        title="Edit"
                        aria-label={`Edit ${params.row.original.name}`}
                        onClick={() => {
                            setUser(params.row.original);
                            window.scrollTo(0, 0);
                            setEditModalState(true);
                        }}
                        className="inline-flex items-center justify-center w-7 h-7 rounded text-gray-800 hover:text-black"
                    >
                        <FiEdit2 size={16} />
                    </button>
                </div>
                ) : null,
        },
    ];

    return (
        <div className="container mx-auto px-4 py-8 block">
            {/* Header Section */}
            <div className="flex justify-between items-center mb-6">
                <button
                    onClick={handleResetFilters}
                    className="flex items-center bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
                >
                    <FiPlus className="mr-2" />
                    Reset Filters
                </button>
            </div>

            {/* Filter Panel */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Name
                        </label>
                        <input
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            name="name"
                            value={name}
                            type="text"
                            placeholder="Search by name"
                            onChange={handleNameChange}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Status
                        </label>
                        <div className="react-select-container">
                            <Select
                                options={statusOptions.map((status, i) => {
                                    return {
                                        label: status.label,
                                        value: status.value,
                                    };
                                })}
                                onChange={handleInputStatus}
                                placeholder="Select Status"
                                {...commonSelectProps}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Branch
                        </label>
                        <div className="react-select-container">
                            <Select
                                options={branchOptions}
                                value={branchOptions.find((option) => option.value === (inputs.branch || "own"))}
                                onChange={(option) => {
                                    setIsLoaded(false);
                                    setInputs((prev) => ({
                                        ...prev,
                                        page: 1,
                                        branch: option?.value || "own",
                                    }));
                                }}
                                placeholder="Select Branch"
                                {...commonSelectProps}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-orange-400">
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.Header}
                                    className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider"
                                >
                                    {column.Header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {usersData ? usersData.map((user) => (
                            <tr key={user._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {user.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {user.phoneNumber}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {user.ic ? user.ic : ""}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {user.tin ? user.tin : ""}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {user.email ? user.email : ""}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                                    {user.type}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                                    {user.status}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {moment(user.createdAt).format("DD/MM/YYYY")}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {canEditListedUser(user) && (
                                    <button
                                        type="button"
                                        title="Edit"
                                        aria-label={`Edit ${user.name}`}
                                        onClick={() => {
                                            const { password, ...restUser } = user;
                                            setUser(restUser);
                                            window.scrollTo(0, 0);
                                            setEditModalState(true);
                                        }}
                                        className="inline-flex items-center justify-center w-7 h-7 rounded text-gray-800 hover:text-black"
                                    >
                                        <FiEdit2 size={16} />
                                    </button>
                                    )}
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={columns.length} className="px-6 py-4 text-center text-gray-500">
                                    Loading...
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                
                {/* Pagination */}
                {totalPages > 0 && (
                    <div className="order-list-table-footer px-4 py-3 flex items-center justify-between border-t border-gray-200">
                        <div className="order-list-table-footer-inner flex-1 flex justify-between items-center">
                            <div className="order-list-table-footer-results text-sm text-gray-700">
                                Showing{' '}
                                <span className="font-medium">
                                    {((inputs.page - 1) * inputs.limit) + 1}
                                </span>
                                {' '}-{' '}
                                <span className="font-medium">
                                    {Math.min(inputs.page * inputs.limit, totalPages)}
                                </span>
                                {' '}of{' '}
                                <span className="font-medium">{totalPages}</span>
                                {' '}results
                            </div>
                            <div className="order-list-table-footer-pagination flex space-x-2">
                                <button
                                    onClick={() => setInputs(prev => ({ ...prev, page: 1 }))}
                                    disabled={inputs.page === 1}
                                    title="First"
                                    aria-label="First page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        inputs.page === 1
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-orange-400 text-white hover:bg-orange-500'
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&lt;&lt;</span>
                                    <span className="order-list-pagination-text">First</span>
                                </button>
                                <button
                                    onClick={() => setInputs(prev => ({ ...prev, page: prev.page - 1 }))}
                                    disabled={inputs.page === 1}
                                    title="Previous"
                                    aria-label="Previous page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        inputs.page === 1
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-orange-400 text-white hover:bg-orange-500'
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&lt;</span>
                                    <span className="order-list-pagination-text">Previous</span>
                                </button>
                                <span className="order-list-pagination-page px-3 py-1 bg-gray-100 text-gray-900 rounded-md text-sm font-medium">
                                    Page {inputs.page} of {Math.ceil(totalPages / inputs.limit)}
                                </span>
                                <button
                                    onClick={() => setInputs(prev => ({ ...prev, page: prev.page + 1 }))}
                                    disabled={inputs.page >= Math.ceil(totalPages / inputs.limit)}
                                    title="Next"
                                    aria-label="Next page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        inputs.page >= Math.ceil(totalPages / inputs.limit)
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-orange-400 text-white hover:bg-orange-500'
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&gt;</span>
                                    <span className="order-list-pagination-text">Next</span>
                                </button>
                                <button
                                    onClick={() => setInputs(prev => ({ ...prev, page: Math.ceil(totalPages / inputs.limit) }))}
                                    disabled={inputs.page >= Math.ceil(totalPages / inputs.limit)}
                                    title="Last"
                                    aria-label="Last page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        inputs.page >= Math.ceil(totalPages / inputs.limit)
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-orange-400 text-white hover:bg-orange-500'
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&gt;&gt;</span>
                                    <span className="order-list-pagination-text">Last</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {editModalState && user && canEditListedUser(user) && (
                <OverlayContainer>
                    <ModalContainer
                        onClick={() => setEditModalState(false)}
                    ></ModalContainer>
                    <Overlay onClick={() => setEditModalState(false)}>
                        <ModalDetails onClick={(event) => event.stopPropagation()}>
                            <ModalItemContainer>
                                <DesktopCloseButton
                                    aria-label="Close edit user form"
                                    onClick={() => setEditModalState(false)}
                                >
                                    <FiX />
                                </DesktopCloseButton>
                                <MobileCloseButton
                                    aria-label="Close edit user form"
                                    onClick={() => setEditModalState(false)}
                                >
                                    <FiX />
                                </MobileCloseButton>
                                <div className="updateTitle">
                                    <form className="addProductForm">
                                        {!isMarketer && (
                                            <>
                                                <div className="addProductItem">
                                                    <label>Full Name</label>
                                                    <input
                                                        name="name"
                                                        type="text"
                                                        placeholder={user.name}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>Email</label>
                                                    <input
                                                        name="email"
                                                        type="text"
                                                        placeholder={user.email}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>Phone Number</label>
                                                    <input
                                                        name="phoneNumber"
                                                        type="text"
                                                        placeholder={user.phoneNumber}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>IC</label>
                                                    <input
                                                        name="ic"
                                                        type="text"
                                                        placeholder={user.ic || "IC Number"}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>TIN</label>
                                                    <input
                                                        name="tin"
                                                        type="text"
                                                        placeholder={user.tin || "Tax Identification Number"}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>Password</label>
                                                    <input
                                                        name="password"
                                                        type="password"
                                                        placeholder={"******"}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>
                                                        Date of Birth (optional)
                                                    </label>
                                                    <input
                                                        name="dob"
                                                        type="date"
                                                        value={
                                                            user.dob
                                                                ? moment(
                                                                      user.dob
                                                                  ).format("YYYY-MM-DD")
                                                                : undefined
                                                        }
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="addProductItem">
                                                    <label>Marketer</label>
                                                    <div className="react-select-container">
                                                        <Select
                                                            options={marketersData.map((marketer) => ({
                                                                label: marketer.name,
                                                                value: marketer._id,
                                                            }))}
                                                            value={
                                                                user.marketer
                                                                    ? {
                                                                          label: marketersData.find(
                                                                              (m) => m._id === user.marketer
                                                                          )?.name || "",
                                                                          value: user.marketer,
                                                                      }
                                                                    : null
                                                            }
                                                            onChange={(selected) => {
                                                                setUser((prev) => ({
                                                                    ...prev,
                                                                    marketer: selected ? selected.value : null,
                                                                }));
                                                            }}
                                                            placeholder="Select Marketer"
                                                            isClearable
                                                            {...commonSelectProps}
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        <div className="addProductItem">
                                            <label>Remarks</label>
                                            <textarea
                                                name="remark"
                                                type="text"
                                                placeholder={user.remark || "Enter remarks"}
                                                onChange={handleChange}
                                                rows="4"
                                            />
                                        </div>
                                        <div
                                            onClick={handleClick}
                                            className="updateProductButton"
                                        >
                                            Update User
                                        </div>
                                    </form>
                                </div>
                            </ModalItemContainer>
                        </ModalDetails>
                    </Overlay>
                </OverlayContainer>
            )}
        </div>
    );
}
